import React, { useMemo } from 'react';
import { Box, Card, CardContent, Typography, useTheme } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
  ResponsiveContainer,
  Cell
} from 'recharts';

interface TicketType {
  name: string;
  quantity: number;
  quantity_sold: number;
  price: number;
  revenue: number;
}

interface TitoEvent {
  title: string;
  totalSold: number;
  totalAvailable: number;
  totalRevenue: number;
  ticketTypes: TicketType[];
  venue?: {
    _id: string;
    name: string;
    capacity: number;
    location: string;
  };
  venueCapacity?: number;
}

interface EventSalesChartProps {
  event: TitoEvent;
}

const EventSalesChart: React.FC<EventSalesChartProps> = ({ event }) => {
  const theme = useTheme();
  
  // Use venue capacity if available, otherwise fall back to totalAvailable
  const capacity = event.venueCapacity || event.totalAvailable;
  const soldPercentage = capacity > 0 ? (event.totalSold / capacity) * 100 : 0;
  const isSoldOut = soldPercentage >= 92;

  // Transform data for Recharts
  const chartData = useMemo(() => {
    return event.ticketTypes.map((type) => {
      return {
        name: type.name,
        sold: type.quantity_sold,
        remaining: Math.max(type.quantity - type.quantity_sold, 0)
      };
    });
  }, [event.ticketTypes]);

  // Gradient colors
  const soldGradientId = 'soldGradient';
  const remainingGradientId = 'remainingGradient';

  // Custom dark-themed tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'rgba(30, 41, 59, 0.95)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            padding: 1.5,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 'bold', mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((pld: any, index: number) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: pld.color, fontSize: '0.875rem' }}
            >
              {pld.name}: {pld.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  return (
    <Card elevation={3} sx={{ mt: 3, background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(51, 65, 85, 0.8) 100%)' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
            Sales Visualization
          </Typography>
          {isSoldOut && (
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.success.main,
                fontWeight: 'bold',
                backgroundColor: 'rgba(46, 125, 50, 0.2)',
                padding: '4px 12px',
                borderRadius: 1,
                border: `1px solid ${theme.palette.success.main}`,
              }}
            >
              SOLD OUT
            </Typography>
          )}
        </Box>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Event:</strong> {event.title}<br/>
          <strong>Total Sold:</strong> {event.totalSold} / {capacity} ({soldPercentage.toFixed(1)}%)<br/>
          <strong>Total Revenue:</strong> €{event.totalRevenue.toFixed(2)}
          {event.venue && (
            <>
              <br/>
              <strong>Venue:</strong> {event.venue.name}
            </>
          )}
        </Typography>
        <Box sx={{ width: '100%', height: 300 }}>
          <ResponsiveContainer>
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <defs>
                <linearGradient id={soldGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#8b5cf6" stopOpacity={1} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0.8} />
                </linearGradient>
                <linearGradient id={remainingGradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={1} />
                  <stop offset="100%" stopColor="#059669" stopOpacity={0.8} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148, 163, 184, 0.2)" />
              <XAxis 
                dataKey="name" 
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.875rem' }}
              />
              <YAxis 
                stroke={theme.palette.text.secondary}
                style={{ fontSize: '0.875rem' }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend 
                wrapperStyle={{ 
                  paddingTop: '10px',
                  fontSize: '0.875rem'
                }}
              />
              <Bar dataKey="sold" stackId="a" fill={`url(#${soldGradientId})`} name="Sold" radius={[0, 0, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-sold-${index}`} />
                ))}
              </Bar>
              <Bar dataKey="remaining" stackId="a" fill={`url(#${remainingGradientId})`} name="Remaining" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => (
                  <Cell key={`cell-remaining-${index}`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default EventSalesChart;